import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import {
  MobileAppBar, MobileToolbar, MobileMenuIconButton, MobileLogoContainer,
  SearchIconButton, MobileNavButton, MobileSubNavButton,
  MobileAuthContainer, MobileButton, MobileCountryContainer,
  MobileCountrySelectBox, MobileDrawerPaper, MobileCountryTypography,
  MobileDrawer, MobileSearchBar, MobileSearchBox,
  MobileHeaderWrapper
} from './styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import MobileExpandedCategories from './mobileExpandedCategories';
import { DRAWER_TRANSITION_TIMEOUT, events, TABS } from '../../constants';
import Resources from './resources';
import UserActions from './notificationItem';
import GlobalSearch from './GlobalSearch';
import { SdfButton } from '@waypoint/react-components';
import { KeyboardArrowDown as ArrowDownIcon, Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from 'react-redux';
import { setActiveTab, setMobileSearchOpen } from '@headless-shell/store';
import { useTranslatedObject } from '../../utils/translation';
import AdpLogo from '../../assets/AdpLogo.svg?url';
import {
  AdpLogoClick, HeaderContactUsTrigger, HeaderLoginInitiate, HeaderMenu,
  HeaderNavigationCountryClick, HeaderNavigationCountrySelection,
  HeaderSearchInitiate, HeaderSubmenuClick, sendGA4Data
} from '../../analytics/events';
import { sendDataToGA4 } from '../../analytics/ga4';
import { CONFIG } from '../../utils/config';

const MobileHeader = memo(({
  authenticated,
  navigate,
  isContactOpen,
  setIsContactOpen,
  renderNavLabel,
  onAboutClick,
  cartData,
  isCartLoading,
  isCartError
}) => {
  // Mobile-specific state management
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [subcategories, setSubcategories] = useState({});
  const [searchOpen, setSearchOpen] = useState(false);
  const dispatch = useDispatch();
  const locale = useSelector(state => state.app.locale);
  const country = useSelector(state => state.app.selectedCountry);
  const activeTab = useSelector(state => state.app.activeTab);
  const header = useTranslatedObject('header');

  const syncSearchOpen = (nextSearchOpen) => {
    setSearchOpen(nextSearchOpen);
    dispatch(setMobileSearchOpen(nextSearchOpen));
  };

  useEffect(() => {
    return () => {
      dispatch(setMobileSearchOpen(false));
    };
  }, []);

  const handleContactClick = () => {
    setIsContactOpen(true);
    sendDataToGA4(HeaderContactUsTrigger, { eventLabel: 'Contact' });
  };

  const resetMobileState = () => {
    setExpandedSection(null);
    setSubcategories({});
  };

  const handleDrawerOpen = () => {
    syncSearchOpen(false);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    resetMobileState();
  };

  // Close the drawer when country selection is changed
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevActiveTabRef.current === TABS.MOBILECOUNTRY && activeTab !== TABS.MOBILECOUNTRY) {
      handleDrawerClose();
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, handleDrawerClose]);

  const handleSectionToggle = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
    setSubcategories({});
    sendGA4Data(HeaderMenu, { eventLabel: section === 'solutions' ? "Solutions" : "Industries" });
  };

  const handleSubcategoryToggle = (categoryName) => {
    setSubcategories(subcategories[categoryName] ? {} : { [categoryName]: true });
    sendGA4Data(HeaderSubmenuClick, {
      customEventName: `${events.EVENT_NAMES.HEADER_SUBMENU}_${activeTab}`,
      eventCategory: activeTab,
      eventLabel: categoryName
    });
  };

  function handleSearchToggle() {
    if (drawerOpen) {
      handleDrawerClose();
    }
    const nextSearchOpen = !searchOpen;
    syncSearchOpen(nextSearchOpen);
    if (nextSearchOpen) {
      sendGA4Data(HeaderSearchInitiate);
    }
  }

  const onLogoClick = () => {
    sendGA4Data(AdpLogoClick);
  };

  function handleLoginClick() {
      sendGA4Data(HeaderLoginInitiate);
      const returnUrl = window.location.pathname + window.location.search;
      window.dispatchEvent(new CustomEvent('auth-request-login', { detail: { returnUrl } }));
      setDrawerOpen(false);
  }

  function onCountryDropdownClick() {
    resetMobileState();
    dispatch(setActiveTab(TABS.MOBILECOUNTRY));
    sendGA4Data(HeaderNavigationCountryClick);
  }

  return (
    <React.Fragment>
      <MobileHeaderWrapper loggedIn={authenticated}>
        <MobileAppBar id="mobile-app-bar" position='relative'>
        <MobileToolbar>
          <MobileMenuIconButton
            id="mobile-menu-toggle"
            edge="start"
            onClick={drawerOpen ? handleDrawerClose : handleDrawerOpen}
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
          >
            {drawerOpen ? <CloseIcon fontSize="large" /> : <MenuIcon fontSize="large" />}
          </MobileMenuIconButton>
          <MobileLogoContainer
            id="mobile-logo-container"
            component="a"
            href={CONFIG.POST_LOGIN_REDIRECT_URI || '/'}
            onClick={onLogoClick}
            aria-label="adp-logo"
          >
            <img id="mobile-logo" src={AdpLogo} alt="ADP" />
          </MobileLogoContainer>
          <SearchIconButton
            id="mobile-search-toggle"
            aria-label="Search"
            active={searchOpen ? 'true' : undefined}
            onClick={handleSearchToggle}
          >
            <SearchIcon fontSize="medium" />
          </SearchIconButton>
          {!authenticated && (
            <SdfButton
              id="mobile-login-button"
              emphasis="primary"
              variant="contained"
              onClick={handleLoginClick}
            >
              {header.login}
            </SdfButton>
          )}
          {/* Authenticated user actions */}
          {authenticated && (
            <React.Fragment>
              <UserActions
                showBookmarks={false}
                showCart={true}
                cartData={cartData}
                isCartLoading={isCartLoading}
                isCartError={isCartError}
                navigate={navigate}
              />
              <Resources navigate={navigate} />
            </React.Fragment>
          )}
        </MobileToolbar>
        </MobileAppBar>
        <MobileSearchBar id="mobile-search-bar" open={searchOpen}>
        <MobileSearchBox>
            <GlobalSearch
              navigate={navigate}
              onSearchComplete={() => {
                syncSearchOpen(false);
              }}
              isAuthenticated={authenticated}
            />
        </MobileSearchBox>
        </MobileSearchBar>
      </MobileHeaderWrapper>
      <MobileDrawer
        id="mobile-drawer"
        anchor="left"
        open={drawerOpen}
        disableEnforceFocus
        onClose={handleDrawerClose}
        isLoggedIn={authenticated}
        slotProps={{
          paper: { component: MobileDrawerPaper },
          transition: { timeout: DRAWER_TRANSITION_TIMEOUT },
        }}
      >
        {!isContactOpen && (
          <>
            <Box id="mobile-nav-container">
              {header?.left_nav?.filter(item => item.key !== 'smart_search')
                .map((item) => {
                  if (
                    item.key === 'solutions' ||
                    item.key === 'industry' ||
                    item.key === 'partners'
                  ) {
                    return (
                      <React.Fragment key={item.key}>
                        <MobileSubNavButton
                          id={`mobile-nav-${item.key}`}
                          fullWidth
                          onClick={() => handleSectionToggle(item.key)}
                          endIcon={expandedSection === item.key ? <RemoveIcon /> : <AddIcon />}
                          aria-expanded={expandedSection === item.key}
                        >
                          {item.label}
                        </MobileSubNavButton>
                        {expandedSection === item.key && (
                          <MobileExpandedCategories
                            expandedState={subcategories}
                            onCategoryToggle={handleSubcategoryToggle}
                            keyPrefix={item.key}
                            navigate={navigate}
                            locale={locale}
                            type={item.key}
                            authenticated={authenticated}
                            handleDrawerClose={handleDrawerClose}
                          />
                        )}
                      </React.Fragment>
                    );
                  }
                  return (
                    <MobileNavButton
                      key={item.id || item.label}
                      id={`mobile-nav-${item.id || item.label.toLowerCase()}`}
                      fullWidth
                      onClick={() => {
                        onAboutClick(item.key);
                        if (item.key === 'apps_home') {
                          handleDrawerClose();
                        }
                      }}
                    >
                      {renderNavLabel(item)}
                    </MobileNavButton>
                  );
                })}
            </Box>
            <MobileAuthContainer id="mobile-auth-container">
              {!authenticated && (
                <MobileButton
                  id="mobile-auth-login"
                  emphasis="primary"
                  onClick={() => navigate('/login')}
                >
                  {header.login}
                </MobileButton>
              )}
              <MobileButton
                id="mobile-contact-button"
                fullWidth
                onClick={handleContactClick}
              >
                {header.contact || "Contact"}
              </MobileButton>
            </MobileAuthContainer>
            <MobileCountryContainer id="mobile-country-container">
              <MobileCountrySelectBox
                id="mobile-country-selector"
                onClick={onCountryDropdownClick}
              >
                <MobileCountryTypography>
                  {country}
                </MobileCountryTypography>
                <ArrowDownIcon />
              </MobileCountrySelectBox>
            </MobileCountryContainer>
          </>
        )}
      </MobileDrawer>
    </React.Fragment>
  );
});

MobileHeader.displayName = 'MobileHeader';

MobileHeader.propTypes = {
  authenticated: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
  handleClick: PropTypes.func,
  cartData: PropTypes.shape({
    items: PropTypes.array
  }),
  isCartLoading: PropTypes.bool,
  isCartError: PropTypes.bool
};

export default MobileHeader;
